// ===========================================
// Parent Calendar Page
// ===========================================
// Read-only calendar showing children's lessons with Day/Week/Month/Term views

import { useState, useMemo, useCallback } from 'react';
import { Calendar, View, Views, Navigate } from 'react-big-calendar';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { format, startOfWeek, addWeeks, isBefore, isAfter } from 'date-fns';
import { Link as RouterLink } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import AddToCalendarButtons from '../../components/events/AddToCalendarButtons';
import { formatEventWhen } from '../../components/events/eventCalendarItem';
import { EVENT_CALENDAR_COLOR } from '../../constants/eventCalendar';
import { useTenant, useSchoolTimezone } from '../../contexts/TenantContext';
import { useMyCalendarEvents, useCalendarTerms } from '../../hooks/useHybridBooking';
import { useMyChildren } from '../../hooks/useParentProfile';
import { CalendarEvent } from '../../api/hybridBooking.api';
import {
  localizer,
  CALENDAR_MIN_TIME,
  CALENDAR_MAX_TIME,
  calendarPaperStyles,
} from '../../components/admin/calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// ===========================================
// TERM VIEW
// ===========================================

type ExtendedView = View | 'term';

interface TermRange {
  start: Date;
  end: Date;
  name: string;
}

// ===========================================
// EVENT DETAIL DIALOG
// ===========================================

interface EventDetailDialogProps {
  event: CalendarEvent | null;
  onClose: () => void;
}

const RSVP_CHIP: Record<string, string> = {
  ATTENDING: 'Going',
  MAYBE: 'Maybe',
  WAITLIST: 'On the waitlist',
};

/** MAYBE / WAITLIST read differently from Going on the item itself, not only in its dialog (D11). */
const itemMarker = (event: CalendarEvent): string => {
  const rsvp = event.resource?.type === 'EVENT' ? event.resource.rsvpResponse : undefined;
  return rsvp === 'MAYBE' ? ' (maybe)' : rsvp === 'WAITLIST' ? ' (waitlist)' : '';
};

/** A school event the family is going to (#256): when, where, their response, share buttons. */
function SchoolEventDetailDialog({ event, onClose }: { event: CalendarEvent; onClose: () => void }) {
  const timeZone = useSchoolTimezone();
  const { schoolSlug } = useTenant();
  const r = event.resource;
  const cancelled = r?.isCancelled === true;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography
          variant="h6"
          component="span"
          sx={{ textDecoration: cancelled ? 'line-through' : undefined }}
        >
          {event.title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>When:</strong> {formatEventWhen(event.startsAtIso, event.endsAtIso, timeZone)}
            </Typography>
          </Grid>
          {r?.venueName && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Where:</strong> {r.venueName}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {cancelled && <Chip label="Cancelled" color="error" size="small" />}
            {r?.rsvpResponse && <Chip label={RSVP_CHIP[r.rsvpResponse] ?? r.rsvpResponse} size="small" />}
            {r?.hasTickets && <Chip label="You have tickets" size="small" sx={{ backgroundColor: '#96DAC9', color: '#080808' }} />}
          </Grid>
          {r?.eventId && event.startsAtIso && (
            <Grid item xs={12}>
              <AddToCalendarButtons
                eventId={r.eventId}
                title={event.title}
                startsAtIso={event.startsAtIso}
                endsAtIso={event.endsAtIso}
                location={r.venueName ?? null}
                available={r.eventStatus === 'PUBLISHED'}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {r?.eventId && (
          <Button component={RouterLink} to={`/${schoolSlug}/parent/events/${r.eventId}`}>
            View event
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function EventDetailDialog({ event, onClose }: EventDetailDialogProps) {
  if (!event) return null;
  if (event.resource?.type === 'EVENT') {
    return <SchoolEventDetailDialog event={event} onClose={onClose} />;
  }
  const r = event.resource;

  return (
    <Dialog open={!!event} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6">{r?.lessonName || event.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {format(new Date(event.start), 'd MMM yyyy')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1}>
          {r?.studentName && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Student:</strong> {r.studentName}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2">
              <strong>Time:</strong> {format(new Date(event.start), 'h:mm a')} –{' '}
              {format(new Date(event.end), 'h:mm a')}
            </Typography>
          </Grid>
          {r?.teacherName && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Teacher:</strong> {r.teacherName}
              </Typography>
            </Grid>
          )}
          {r?.roomName && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Room:</strong> {r.roomName}
              </Typography>
            </Grid>
          )}
          {r?.instrumentName && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Instrument:</strong> {r.instrumentName}
              </Typography>
            </Grid>
          )}
          {r?.programLevelName && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Level:</strong> {r.programLevelName}
              </Typography>
            </Grid>
          )}
          {r?.isCancelled && (
            <Grid item xs={12}>
              <Chip label="Cancelled" color="error" size="small" />
            </Grid>
          )}
          {r?.isAbsenteeReleased && (
            <Grid item xs={12}>
              <Chip label="Absent — slot released for rebooking" color="warning" size="small" />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// PARENT TOOLBAR
// ===========================================

interface ParentCalendarToolbarProps {
  date: Date;
  view: ExtendedView;
  onNavigate: (action: typeof Navigate[keyof typeof Navigate] | 'term-prev' | 'term-next') => void;
  onView: (view: ExtendedView) => void;
  currentTermName?: string;
}

function ParentCalendarToolbar({ date, view, onNavigate, onView, currentTermName }: ParentCalendarToolbarProps) {
  const label = useMemo(() => {
    if (view === 'term') return currentTermName || 'Term';
    if (view === Views.MONTH) return format(date, 'MMMM yyyy');
    // week view
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay() + 1);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  }, [date, view, currentTermName]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button size="small" variant="outlined" onClick={() => onNavigate(Navigate.TODAY)}>
          Today
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onNavigate(view === 'term' ? 'term-prev' : Navigate.PREVIOUS)}
        >
          Back
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => onNavigate(view === 'term' ? 'term-next' : Navigate.NEXT)}
        >
          Next
        </Button>
      </Box>

      <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
        {label}
      </Typography>

      <ToggleButtonGroup
        value={view}
        exclusive
        onChange={(_, v) => v && onView(v)}
        size="small"
      >
        <ToggleButton value={Views.WEEK}>Week</ToggleButton>
        <ToggleButton value={Views.MONTH}>Month</ToggleButton>
        <ToggleButton value="term">Term</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function ParentCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<ExtendedView>(() => {
    try {
      const saved = localStorage.getItem('parentCalendarViewPreference');
      if (saved && [Views.WEEK, Views.MONTH, 'term'].includes(saved as ExtendedView)) {
        return saved as ExtendedView;
      }
    } catch {
      // Ignore localStorage errors
    }
    return Views.WEEK;
  });
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [termIndex, setTermIndex] = useState(0);

  const { data: calendarEvents = [], isLoading } = useMyCalendarEvents();
  const { data: myStudents = [] } = useMyChildren();
  const { data: terms = [] } = useCalendarTerms();

  // Sort terms by start date
  const sortedTerms: TermRange[] = useMemo(() => {
    return [...terms]
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .map((t) => ({
        start: new Date(t.startDate),
        end: new Date(t.endDate),
        name: t.name,
      }));
  }, [terms]);

  // Current term for "Term" view
  const currentTerm = sortedTerms[termIndex] ?? null;

  // Filter out hybrid placeholders
  const events = useMemo(
    () => calendarEvents.filter((e) => e.resource?.type !== 'HYBRID_PLACEHOLDER'),
    [calendarEvents]
  );

  // For non-term views just pass all events through; term view bucketing handles filtering
  const visibleEvents = events;

  // Event style by student colour (cycle through brand accent colours)
  const studentColors = ['#4580E4', '#96DAC9', '#FFAE9E', '#FFCE00', '#a3d9f6'];
  const studentColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    myStudents.forEach((s, i) => {
      map[s.firstName] = studentColors[i % studentColors.length];
    });
    return map;
  }, [myStudents]);

  const eventStyleGetter = useCallback(
    (event: CalendarEvent) => {
      const studentName = event.resource?.studentName || '';
      const firstName = studentName.split(' ')[0];
      const isSchoolEvent = event.resource?.type === 'EVENT';
      const bg = isSchoolEvent ? EVENT_CALENDAR_COLOR : studentColorMap[firstName] || '#4580E4';
      const isCancelled = event.resource?.isCancelled;
      const isAbsenteeReleased = event.resource?.isAbsenteeReleased;
      return {
        style: {
          backgroundColor: isAbsenteeReleased ? '#9DA5AF' : bg,
          borderRadius: '4px',
          color: isSchoolEvent ? '#080808' : '#fff',
          fontSize: '12px',
          padding: '2px 4px',
          opacity: isCancelled || isAbsenteeReleased ? 0.5 : 1,
          textDecoration: isCancelled ? 'line-through' : undefined,
          cursor: 'pointer',
        },
      };
    },
    [studentColorMap]
  );

  const handleNavigate = useCallback(
    (action: typeof Navigate[keyof typeof Navigate] | 'term-prev' | 'term-next') => {
      if (action === 'term-prev') {
        setTermIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (action === 'term-next') {
        setTermIndex((i) => Math.min(sortedTerms.length - 1, i + 1));
        return;
      }
      // Standard rbc navigate — compute new date based on current view
      if (action === Navigate.TODAY) return setCurrentDate(new Date());
      const d = new Date(currentDate);
      if (currentView === Views.WEEK) {
        d.setDate(d.getDate() + (action === Navigate.PREVIOUS ? -7 : 7));
      } else {
        d.setMonth(d.getMonth() + (action === Navigate.PREVIOUS ? -1 : 1));
      }
      setCurrentDate(d);
    },
    [currentDate, currentView, sortedTerms.length]
  );

  const handleViewChange = useCallback((view: ExtendedView) => {
    if (view === 'term') {
      // Jump term index to the one covering today
      const today = new Date();
      const idx = sortedTerms.findIndex(
        (t) => today >= t.start && today <= t.end
      );
      setTermIndex(idx >= 0 ? idx : 0);
    }
    setCurrentView(view);
    try {
      localStorage.setItem('parentCalendarViewPreference', view);
    } catch {
      // Ignore localStorage errors
    }
  }, [sortedTerms]);

  // For term view: group events by term week number
  const termWeeks = useMemo(() => {
    if (currentView !== 'term' || !currentTerm) return [];

    const sorted = [...visibleEvents].sort(
      (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );

    // Build week buckets from term start (week 1 = first Mon on or before term start)
    const termWeekStart = startOfWeek(currentTerm.start, { weekStartsOn: 1 });
    const weeks: Array<{ weekNum: number; weekStart: Date; weekEnd: Date; events: CalendarEvent[] }> = [];

    let weekStart = termWeekStart;
    let weekNum = 1;
    while (!isAfter(weekStart, currentTerm.end)) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);
      weeks.push({ weekNum, weekStart: new Date(weekStart), weekEnd, events: [] });
      weekStart = addWeeks(weekStart, 1);
      weekNum++;
    }

    // Assign each event to its week bucket
    sorted.forEach((event) => {
      const eventDate = new Date(event.start);
      const bucket = weeks.find(
        (w) => !isBefore(eventDate, w.weekStart) && !isAfter(eventDate, w.weekEnd)
      );
      if (bucket) bucket.events.push(event);
    });

    // Only return weeks that have events
    return weeks.filter((w) => w.events.length > 0);
  }, [currentView, visibleEvents, currentTerm]);

  // Student legend
  const legend = useMemo(
    () =>
      myStudents.map((s, i) => ({
        name: s.firstName,
        color: studentColors[i % studentColors.length],
      })),
    [myStudents]
  );

  return (
    <Box>
      <PageHeader
        title="Calendar"
        subtitle="Your family's lessons and events"
        breadcrumbs={[{ label: 'Dashboard', path: '..' }, { label: 'Calendar' }]}
      />

      {/* Student colour legend */}
      {legend.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {legend.map((l) => (
            <Chip
              key={l.name}
              icon={<PersonIcon />}
              label={l.name}
              size="small"
              sx={{ backgroundColor: l.color, color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            />
          ))}
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : calendarEvents.length === 0 ? (
        <Alert severity="info" icon={<CalendarTodayIcon />}>
          Nothing on the calendar for this period. Contact the school if you think this is an error.
        </Alert>
      ) : (
        <>
          <ParentCalendarToolbar
            date={currentDate}
            view={currentView}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            currentTermName={currentTerm?.name}
          />

          {currentView === 'term' ? (
            // Term view: grouped by week
            <Box>
              {currentTerm && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  {format(currentTerm.start, 'd MMM yyyy')} – {format(currentTerm.end, 'd MMM yyyy')}
                </Typography>
              )}
              {termWeeks.length === 0 ? (
                <Paper sx={{ p: 2 }}>
                  <Typography color="text.secondary">Nothing on the calendar this term.</Typography>
                </Paper>
              ) : (
                termWeeks.map((week) => (
                  <Paper key={week.weekNum} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                      Week {week.weekNum}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {format(week.weekStart, 'd MMM')} – {format(week.weekEnd, 'd MMM')}
                      </Typography>
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    {week.events.map((event, idx) => {
                      const isSchoolEvent = event.resource?.type === 'EVENT';
                      const bg = isSchoolEvent
                        ? EVENT_CALENDAR_COLOR
                        : studentColorMap[event.resource?.studentName?.split(' ')[0] || ''] || '#4580E4';
                      return (
                        <Box
                          key={idx}
                          onClick={() => setSelectedEvent(event)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            p: 1,
                            mb: 0.5,
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'action.hover' },
                            borderLeft: `4px solid ${bg}`,
                          }}
                        >
                          <Box sx={{ minWidth: 130 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {format(new Date(event.start), 'EEE, d MMM')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(event.start), 'h:mm a')} – {format(new Date(event.end), 'h:mm a')}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ textDecoration: isSchoolEvent && event.resource?.isCancelled ? 'line-through' : undefined }}
                            >
                              {(event.resource?.lessonName || event.title) + itemMarker(event)}
                            </Typography>
                            {event.resource?.studentName && (
                              <Typography variant="caption" color="text.secondary">
                                {event.resource.studentName}
                              </Typography>
                            )}
                          </Box>
                          {event.resource?.isCancelled && (
                            <Chip label="Cancelled" color="error" size="small" />
                          )}
                          {event.resource?.isAbsenteeReleased && (
                            <Chip label="Absent" color="warning" size="small" />
                          )}
                        </Box>
                      );
                    })}
                  </Paper>
                ))
              )}
            </Box>
          ) : (
            <Paper sx={{ ...calendarPaperStyles }}>
              <Calendar
                localizer={localizer}
                events={visibleEvents}
                view={currentView as View}
                date={currentDate}
                onView={(v) => setCurrentView(v)}
                onNavigate={setCurrentDate}
                eventPropGetter={eventStyleGetter}
                titleAccessor={(event) => (event as CalendarEvent).title + itemMarker(event as CalendarEvent)}
                onSelectEvent={(event) => setSelectedEvent(event as CalendarEvent)}
                min={CALENDAR_MIN_TIME}
                max={CALENDAR_MAX_TIME}
                toolbar={false}
                popup
              />
            </Paper>
          )}
        </>
      )}

      <EventDetailDialog event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </Box>
  );
}
